import type { ReactNode } from 'react';
import { useLocale, useT } from '../i18n/locale-context';
import {
  ConcentrationPill,
  MarketShareBar,
  MetricCell,
  MetricGrid,
  PriceBandBar,
  SEGMENT_BG,
  ShareTile,
  SignalRow,
  type ShareSegment,
} from './analytics-card-parts';
import {
  BlurredValue,
  PLACEHOLDER_COUNT,
  PLACEHOLDER_DIGIT,
  PLACEHOLDER_HHI,
  PLACEHOLDER_ID,
  PLACEHOLDER_PCT,
  placeholderAmount,
} from './blurred-value';
import { LOCKED_CHIP_CLASS } from './locked-chip';

/**
 * The bodies of the three locked Pro cards on a tender page: market data,
 * buyer-supplier concentration, procedure parameters.
 *
 * Each renders the REAL card's layout through the shared parts in
 * `analytics-card-parts.tsx`, with every value replaced by a blurred, all-8
 * placeholder. So a visitor sees the shape of the answer — two stat tiles and a
 * price band, a concentration bar over ranked suppliers, the procedure checks —
 * without being able to read it, and a devtools un-blur yields «88,8 %», not a
 * number about this tender.
 *
 * What makes the card credible is NOT the fake figures — it is the one true
 * line the panel puts underneath (`note` on `AnalyticsTeaser`), built from the
 * server's non-decision counts.
 *
 * Everything here is deterministic: fixed placeholder strings and fixed segment
 * widths, no randomness, so SSR and the hydrated client render identically.
 */

/** Fill position of the blurred price band. Chosen once; NOT derived from any
 *  tender, and never sent by the server. */
const BAND_FILL_PCT = 55;

/** Rows a teaser list draws: the real count capped at the real card's `max`,
 *  or `max` when the count is unknown (an API that predates it). */
function teaserRowCount(count: number | undefined, max: number): number {
  return count != null && count > 0 ? Math.min(count, max) : max;
}

/**
 * Widths of the blurred share segments — deliberately FLAT.
 *
 * ⚠️ They used to be 55/20/10/8/7, i.e. a plausible concentrated market. The
 * widths are the one part of this card that is NOT blurred, so that shape was
 * legible: every buyer appeared to have a dominant favourite supplier, on every
 * tender, before anyone paid a som (Igor, 2026-09-18). A teaser may show that
 * the analysis exists; it may not answer it, and «у этого заказчика есть
 * любимчик» is the answer the card sells.
 *
 * 24/22/20/18/16 rather than five exact 20s: it keeps the rank ordering the
 * colour ramp already implies (segment 1 ≥ segment 5, as in any real top-N
 * breakdown) while the 1.5× spread between first and last is far too mild to
 * read as dominance. Sums to 100.
 */
const SEGMENT_WIDTHS_PCT = [24, 22, 20, 18, 16];

/** «Рыночные данные» — the base-rates card: median participants, median
 *  winning price, and the winning-price band. */
export function MarketTeaserBody({
  metrics,
}: {
  /** Which metrics the real panel has (`BaseRatesResponse.metrics`). The teaser
   *  blurs exactly those: a blurred «Медиана участников» over results that
   *  carry no participant counts — every Kazakh lot — is a promise Pro then
   *  shows as «—» (owner, 2026-10-15). Absent (older API) = all, as before. */
  metrics?: { participants: boolean; price: boolean };
} = {}) {
  const t = useT();
  const { locale } = useLocale();
  const showParticipants = metrics?.participants ?? true;
  const showPrice = metrics?.price ?? true;
  if (!showParticipants && !showPrice) return null;

  return (
    <>
      <MetricGrid single={!(showParticipants && showPrice)}>
        {showParticipants && (
          <MetricCell
            label={t('baseRates.competition')}
            value={<BlurredValue>{PLACEHOLDER_COUNT}</BlurredValue>}
          />
        )}
        {showPrice && (
          <MetricCell
            label={t('baseRates.priceMedian')}
            value={<BlurredValue>{placeholderAmount(locale)}</BlurredValue>}
          />
        )}
      </MetricGrid>
      {showPrice && (
        <PriceBandBar
          label={t('baseRates.priceBand')}
          fillPct={BAND_FILL_PCT}
          low={<BlurredValue>{placeholderAmount(locale, 'small')}</BlurredValue>}
          high={<BlurredValue>{placeholderAmount(locale)}</BlurredValue>}
        />
      )}
    </>
  );
}

/** «Концентрация поставщиков заказчика» — the buyer-graph card: verdict pill, two
 *  verdict tiles, the distribution bar and the ranked supplier rows.
 *
 *  The rows carry the buyer's REAL first supplier name when the API sends it (one since 2026-10-19, three before)
 *  (`names`, owner 2026-10-18 — a more convincing wall): plain text, never a
 *  link (the whole card is the /plans link), every figure still blurred, and one
 *  caption under the list saying what Pro adds. Without names — an older API —
 *  they read «Поставщик № 1..3», as before. */
export function ConcentrationTeaserBody({
  suppliers,
  names,
}: {
  /** The buyer's distinct-supplier count from the locked payload. The teaser
   *  draws at most that many supplier rows: three blurred rows over a buyer
   *  with one supplier is a list Pro never gets (owner, 2026-10-15). Absent
   *  (older API, or no count) = three, as before. */
  suppliers?: number;
  /** The first supplier name(s) from the locked payload (`teaser`); when present
   *  they ARE the rows, so their count is the row count. */
  names?: readonly string[];
} = {}) {
  const t = useT();
  const real = names && names.length > 0 ? names : null;
  const rowCount = real ? real.length : teaserRowCount(suppliers, 3);
  const { locale } = useLocale();

  const segments: ShareSegment[] = SEGMENT_WIDTHS_PCT.map((widthPct, i) => ({
    key: `seg-${i}`,
    className: SEGMENT_BG[Math.min(i, SEGMENT_BG.length - 1)]!,
    widthPct,
  }));

  return (
    <div className="border-t border-edge-light px-5 py-4 sm:px-6">
      {/* The pill sits on the card header in the real layout; the teaser's
          header belongs to AnalyticsTeaser, so it leads the body instead.
          Neutral band on purpose — a red or green pill would leak the verdict
          without anyone reading a number. */}
      <div className="mb-3 flex justify-end">
        <ConcentrationPill level="unknown">
          {t('supplierGraph.teaserConcentration')}
          {/* 3 px, like the procedure pills: a 4 px halo is wider than the
              pill's 8 px padding and reads as a smudged border. */}
          {/* No «HHI» even blurred: the word is off the screen everywhere
              (Igor, 2026-10-07). The blurred slot stays an all-8s figure. */}
          <BlurredValue blurPx={3} className="tabular-nums">
            {' '}
            · {PLACEHOLDER_HHI}
          </BlurredValue>
        </ConcentrationPill>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-2.5">
        <ShareTile
          label={t('supplierGraph.topShare')}
          value={<BlurredValue>{PLACEHOLDER_PCT}</BlurredValue>}
          basis={
            <BlurredValue>
              {t('supplierGraph.topShareBasis', {
                wins: PLACEHOLDER_DIGIT,
                total: PLACEHOLDER_COUNT,
              })}
            </BlurredValue>
          }
        />
        <ShareTile
          label={t('supplierGraph.top3Share')}
          value={<BlurredValue>{PLACEHOLDER_PCT}</BlurredValue>}
          basis={
            <BlurredValue>
              {t('supplierGraph.suppliersBasis', { n: PLACEHOLDER_COUNT })}
            </BlurredValue>
          }
        />
      </div>

      <div className="mb-1 flex items-baseline justify-between gap-2 text-[11px] text-ink-muted">
        <span>{t('supplierGraph.marketShare')}</span>
        <BlurredValue className="tabular-nums">
          {t('supplierGraph.topNShare', { n: PLACEHOLDER_COUNT, pct: PLACEHOLDER_COUNT })}
        </BlurredValue>
      </div>
      <MarketShareBar segments={segments} />

      <ul className="mt-3.5 flex flex-col">
        {Array.from({ length: rowCount }, (_, i) => i).map((i) => (
          <li
            key={i}
            className="flex items-center gap-2.5 border-t border-edge-light py-2.5 first:border-t-0 first:pt-0.5"
          >
            <span className={`h-2 w-2 shrink-0 rounded-[3px] ${SEGMENT_BG[i]!}`} aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-semibold text-ink">
                {real ? real[i] : t('supplierGraph.teaserSupplierN', { n: i + 1 })}
              </div>
              <div className="mt-0.5 text-[11px] text-ink-muted">
                <BlurredValue>
                  {t('supplierGraph.contracts.many', { n: PLACEHOLDER_COUNT })}
                </BlurredValue>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-[13.5px] font-bold tabular-nums text-ink">
                <BlurredValue>{PLACEHOLDER_PCT}</BlurredValue>
              </div>
              <div className="text-[11px] tabular-nums text-ink-muted">
                <BlurredValue>
                  {placeholderAmount(locale)} {t('tender.budget.unit')}
                </BlurredValue>
              </div>
            </div>
          </li>
        ))}
      </ul>
      {real && <TeaserNamesCaption className="mt-2.5" />}
    </div>
  );
}

/** «Доли, даты и весь список — в Pro» — under a teaser that shows real names. */
function TeaserNamesCaption({ className }: { className: string }) {
  const t = useT();
  return (
    <p className={`text-[11.5px] text-ink-muted ${className}`}>{t('teaser.companiesCaption')}</p>
  );
}

/** «Вероятные участники» (was «Кто выигрывает такие лоты») — one-line company
 *  rows, like the real card. With `names` (owner, 2026-10-18) the rows are the
 *  REAL first three companies — plain text, never a link (the whole card is the
 *  /plans link), the figures still blurred — under one caption saying what Pro
 *  adds. Without them (an older API) they read «Компания № 1..5», as before. */
export function SimilarWinnersTeaserBody({
  winners,
  names,
}: {
  /** Distinct winners from the locked payload; the real card lists at most
   *  five, so the teaser draws min(winners, 5) rows — never five over a card
   *  that will show three. Absent = five, as before. */
  winners?: number;
  /** The first name(s) from the locked payload (one since 2026-10-19) (`teaser`); when present
   *  they ARE the rows, so their count is the row count. */
  names?: readonly string[];
} = {}) {
  const t = useT();
  const real = names && names.length > 0 ? names : null;
  const rowCount = real ? real.length : teaserRowCount(winners, 5);
  return (
    <div className="border-t border-edge-light px-5 py-2 sm:px-6">
      <ul className="flex flex-col">
        {Array.from({ length: rowCount }, (_, i) => i).map((i) => (
          <li
            key={i}
            className="flex items-center gap-2 border-t border-edge-light py-1.5 first:border-t-0"
          >
            <span className="w-3.5 shrink-0 text-[11.5px] font-bold tabular-nums text-ink-muted">
              {i + 1}
            </span>
            <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-semibold text-ink">
              {real ? real[i] : t('similarWinners.teaserCompanyN', { n: i + 1 })}
            </span>
            <span className="shrink-0 whitespace-nowrap text-[11.5px] tabular-nums text-ink-muted">
              <BlurredValue>
                {t('similarWinners.wins.many', { n: PLACEHOLDER_COUNT })} · {PLACEHOLDER_PCT}
              </BlurredValue>
            </span>
          </li>
        ))}
      </ul>
      {real && <TeaserNamesCaption className="pb-1 pt-1.5" />}
    </div>
  );
}

/** A neutral pill with a blurred figure inside — the chrome stays sharp so the
 *  row still reads as a row; only the finding is unreadable.
 *
 *  `overflow-hidden` on the pill is load-bearing: without it the 4 px halo
 *  spilled past the rounded border and the pill looked smudged rather than
 *  gated. The inner blur is 3 px for the same reason — the pill's padding is
 *  only 8 px wide. */
function TeaserPill({ children }: { children: ReactNode }) {
  return (
    // The shared hidden-data chip (`locked-chip.tsx`) — the outcome wall's
    // «в Pro» chip is this same pill, so the two cannot drift apart.
    <span className={LOCKED_CHIP_CLASS}>
      <BlurredValue blurPx={3}>{children}</BlurredValue>
    </span>
  );
}

/** «Параметры процедуры» — the integrity card: the re-listing row and the
 *  deposit row, each with a blurred pill where the finding would be. */
export function ProcedureTeaserBody() {
  const t = useT();

  return (
    <div className="border-t border-edge-light px-5 py-4 sm:px-6">
      <ul className="flex flex-col gap-2">
        {/* Neutral (un-flagged) styling on both rows: the real card tints a row
            when the signal is notable, so tinting here would answer the
            question the card is gating. */}
        <SignalRow
          icon="relist"
          label={t('tenderIntegrity.teaserRelisting')}
          value={<TeaserPill>{PLACEHOLDER_ID}</TeaserPill>}
        />
        <SignalRow
          icon="shield"
          label={t('tenderIntegrity.teaserDeposit')}
          value={<TeaserPill>{PLACEHOLDER_PCT}</TeaserPill>}
        />
      </ul>
    </div>
  );
}
